import React from 'react';
import Brand from './Brand';

// Ported verbatim from the plugin's src/Components/ProfileTemplate.jsx —
// same icon paths, same class names, same structure. This is what makes the
// rendered profile pixel-identical to the plugin's public profile view;
// don't restyle this with antd components, that's what caused the drift.

function initials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'AC';
  return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
}

function Icon({ name }) {
  const p = {
    pin: 'M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z',
    mail: 'M3 5h18v14H3V5Zm2 2v.5l7 4.5 7-4.5V7H5Zm14 2.8-7 4.5-7-4.5V17h14V9.8Z',
    link: 'M3.9 12a3.1 3.1 0 0 1 3.1-3.1h3V7H7a5 5 0 0 0 0 10h3v-1.9H7A3.1 3.1 0 0 1 3.9 12Zm5.1 1h6v-2H9v2Zm8-6h-3v1.9h3A3.1 3.1 0 0 1 17 13h-3v1.9h3a5 5 0 0 0 0-10Z',
    phone: 'M6.6 10.8a15 15 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 0 1 3 4c0-.6.4-1 1-1h3.4c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.4 0 .8-.3 1l-2.1 2.2Z',
    check: 'M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2Z',
    spark: 'M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2Z',
  }[name];
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d={p} />
    </svg>
  );
}

// rec is expected in the same camelCase shape ProfileTemplate always used
// (name, title, photoUrl, aiNative, anonymize, experience: [{period,
// organization, role}], projects: [{name, description}], etc.) — the parent
// component maps the API's PascalCase fields into this shape before render.
export default function ProfileTemplate({ rec }) {
  const photo = (rec.photoUrl || '').trim();
  return (
    <div className="card" id="profile-card">
      {/* HERO */}
      <header className="hero">
        <div className="hero-top">
          <Brand light />
          {rec.aiNative && (
            <span className="ai-badge">
              <span className="dot" /> AI-Accelerated Delivery
            </span>
          )}
        </div>
        <div className="hero-main">
          {photo ? (
            <img className="avatar" src={photo} alt={rec.name} />
          ) : (
            <div className="avatar-fallback">{initials(rec.name)}</div>
          )}
          <div>
            <div className="hero-name">{rec.name || 'Consultant'}</div>
            <div className="hero-title">{rec.title}</div>
            <div className="hero-meta">
              {rec.location && (<span><Icon name="pin" /> {rec.location}</span>)}
              {rec.email && (<span><Icon name="mail" /> {rec.email}</span>)}
              {rec.phone && (<span><Icon name="phone" /> {rec.phone}</span>)}
              {rec.website && (<span><Icon name="link" /> {rec.website}</span>)}
            </div>
          </div>
        </div>
      </header>

      {/* BODY */}
      <div className="body">
        <div className="main-col">
          {rec.summary && (
            <section className="section">
              <h2 className="section-h">Profile Summary</h2>
              <p className="summary">{rec.summary}</p>
            </section>
          )}

          {rec.highlights?.length > 0 && (
            <section className="section">
              <h2 className="section-h">Key Highlights</h2>
              <ul className="hl-list">
                {rec.highlights.map((h, i) => (<li key={i}>{h}</li>))}
              </ul>
            </section>
          )}

          {rec.experience?.length > 0 && (
            <section className="section">
              <h2 className="section-h">Experience</h2>
              <div className="timeline">
                {rec.experience.map((e, i) => (
                  <div className="exp" key={i}>
                    <div className="exp-period">{e.period}</div>
                    <div className="exp-org">{e.organization}</div>
                    <div className="exp-role">{e.role}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {rec.skills?.length > 0 && (
            <section className="section">
              <h2 className="section-h">Tools &amp; Skills</h2>
              <div className="chips">
                {rec.skills.map((s, i) => (<span className="chip" key={i}>{s}</span>))}
              </div>
            </section>
          )}

          {rec.industries?.length > 0 && (
            <section className="section">
              <h2 className="section-h">Industries</h2>
              <div className="chips">
                {rec.industries.map((s, i) => (<span className="chip navy" key={i}>{s}</span>))}
              </div>
            </section>
          )}
        </div>

        <aside className="side-col">
          {rec.projects?.length > 0 && (
            <div className="side-block">
              <h2 className="section-h">Selected Projects</h2>
              {rec.projects.map((p, i) => (
                <div className="proj" key={i}>
                  <div className="proj-name">{p.name}</div>
                  {p.description && <div className="proj-desc">{p.description}</div>}
                </div>
              ))}
            </div>
          )}

          {rec.certificates?.length > 0 && (
            <div className="side-block">
              <h2 className="section-h">Certifications</h2>
              <ul className="cert-list">
                {rec.certificates.map((c, i) => (
                  <li key={i}><Icon name="check" /> {c}</li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      {/* FOOTER */}
      <footer className="foot">
        <Brand light />
        <div className="foot-contact">
          {!rec.anonymize && rec.email && (<span><Icon name="mail" /> {rec.email}</span>)}
          <span><Icon name="link" /> www.aptclouds.com</span>
          <span><Icon name="spark" /> Senior consultants, AI-accelerated delivery</span>
        </div>
      </footer>
    </div>
  );
}